/**
 * Input validation utilities for security
 */

export const sanitizeHtml = (input: string): string => {
  const element = document.createElement('div');
  element.textContent = input;
  return element.innerHTML;
};

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) && email.length <= 254;
};

export const validatePhone = (phone: string): boolean => {
  const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
  return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
};

export const validateUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

export const sanitizeSearchQuery = (query: string): string => {
  return query
    .replace(/[<>'"&]/g, '')
    .trim()
    .substring(0, 100);
};

export const validateCreditCard = (cardNumber: string): boolean => {
  const cleaned = cardNumber.replace(/\D/g, '');
  
  if (cleaned.length < 13 || cleaned.length > 19) {
    return false;
  }
  
  // Luhn algorithm
  let sum = 0;
  let shouldDouble = false;
  
  for (let i = cleaned.length - 1; i >= 0; i--) {
    let digit = parseInt(cleaned.charAt(i), 10);
    
    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }
    
    sum += digit;
    shouldDouble = !shouldDouble;
  }
  
  return sum % 10 === 0;
};

export const escapeHtml = (unsafe: string): string => {
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

export const validateApiKey = (apiKey: string): boolean => {
  // Basic API key validation - should be at least 32 characters
  return apiKey && apiKey.length >= 32 && /^[A-Za-z0-9_-]+$/.test(apiKey);
};

export const sanitizeInput = (input: string, maxLength: number = 255): string => {
  const trimmed = input.trim();
  
  // Check for potential security threats
  if (detectSqlInjection(trimmed) || detectXssAttempt(trimmed)) {
    if (typeof window !== 'undefined') {
      import('./securityLogger').then(({ securityLogger }) => {
        securityLogger.logSuspiciousActivity('Potential injection attempt detected', { input: trimmed.substring(0, 100) });
      });
    }
    // Return sanitized version, removing suspicious content
    return escapeHtml(trimmed.replace(/[<>'";&]/g, '')).substring(0, maxLength);
  }
  
  return escapeHtml(trimmed).substring(0, maxLength);
};

export const validatePassword = (password: string): boolean => {
  // Minimum 8 characters, at least one uppercase, lowercase, number, and special character
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
  return passwordRegex.test(password);
};

export const rateLimit = (() => {
  const attempts = new Map<string, { count: number; lastAttempt: number }>();
  
  return (key: string, maxAttempts: number = 5, windowMs: number = 300000): boolean => {
    const now = Date.now();
    const record = attempts.get(key);
    
    // Clean up old entries periodically
    if (attempts.size > 1000) {
      for (const [k, v] of attempts.entries()) {
        if (now - v.lastAttempt > windowMs * 2) {
          attempts.delete(k);
        }
      }
    }
    
    if (!record || now - record.lastAttempt > windowMs) {
      attempts.set(key, { count: 1, lastAttempt: now });
      return true;
    }
    
    if (record.count >= maxAttempts) {
      // Log rate limit exceeded
      if (typeof window !== 'undefined') {
        import('./securityLogger').then(({ securityLogger }) => {
          securityLogger.logRateLimitExceeded(key, 'client-side');
        });
      }
      return false;
    }
    
    record.count++;
    record.lastAttempt = now;
    return true;
  };
})();

export const validateFileUpload = (file: File, maxSize: number = 10485760, allowedTypes: string[] = []): boolean => {
  if (file.size > maxSize) {
    return false;
  }
  
  if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
    return false;
  }
  
  return true;
};

export const detectSqlInjection = (input: string): boolean => {
  const sqlPatterns = [
    /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|UNION|SCRIPT)\b)/i,
    /(;|--|\/\*|\*\/|'|")/,
    /(\bOR\b.*=.*\bOR\b)/i,
    /(\b1\s*=\s*1\b)/i
  ];
  
  return sqlPatterns.some(pattern => pattern.test(input));
};

export const detectXssAttempt = (input: string): boolean => {
  const xssPatterns = [
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    /javascript:/gi,
    /on\w+\s*=/gi,
    /<iframe/gi,
    /<object/gi,
    /<embed/gi
  ];
  
  return xssPatterns.some(pattern => pattern.test(input));
};